import React, { createContext, useContext, useState, useEffect } from "react";
import { api } from "../lib/api";

interface User {
  id: number;
  email: string;
  name: string;
  full_name?: string;
  role: string;
  // Partner fields
  partner_id?: string;
  partner_tier?: string;
  partner_name?: string;
  partner?: {
    id: string;
    name: string;
    tier: string;
    email: string;
  };
  // Client fields
  client_id?: string;
  client_name?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refetchUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = async () => {
    try {
      // Note: Session cookie is automatically sent via credentials: 'include'
      // No need to check sessionStorage for session token
      const userData = await api.getCurrentUser();
      setUser(userData);

      // Store user data in session storage for quick access
      sessionStorage.setItem("partner_user", JSON.stringify(userData));
    } catch (error: unknown) {
      console.error("Failed to fetch user:", error);

      // Clear session if 401 (unauthorized/expired)
      if ((error as { status?: number })?.status === 401) {
        sessionStorage.removeItem("partner_user");
        console.log("Session expired or invalid, user logged out");
      }

      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const login = async () => {
    // Redirect to backend OAuth login endpoint
    // Backend will redirect to Authentik and handle the OAuth flow
    // After successful authentication, backend sets session cookie
    // Use relative URL so it works in all environments (local dev, server, production)
    window.location.href = "/auth/login";
  };

  const logout = async () => {
    try {
      await api.logout();

      // Clear session storage
      sessionStorage.removeItem("partner_user");
      sessionStorage.removeItem("oauth_state");

      setUser(null);
      window.location.href = "/";
    } catch (error) {
      console.error("Logout failed:", error);

      // Clear session even if API call fails
      sessionStorage.removeItem("partner_user");
      setUser(null);
    }
  };

  const refetchUser = async () => {
    setIsLoading(true);
    await fetchUser();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        refetchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
